import { useState, useEffect, useCallback } from 'react';
import { ReactComponent as PlusIcon } from '../../../assets/icons/PlusSquare.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/ArrowsCounterClockwise.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/ArrowsOutIn.svg';
import { useProject } from '../../../context/ProjectContext';
import { useTeamContext } from '../../../context/TeamContext';
import ProjectComposer from '../../Projects/ProjectComposer';
import ProjectDetailsModal from '../../Projects/ProjectDetailsModal';
import { useI18n } from '../../../i18n';

const PER_PAGE = 10;

/* ── Skeleton cards ── */
const ProjSkeleton = () => (
    <>
        {[1, 2, 3, 4].map(i => (
            <div key={i} className="proj-item proj-skeleton">
                <div className="proj-skel-name" />
                <div className="proj-skel-sub" />
            </div>
        ))}
    </>
);

const ProjectsWidget = () => {
    const { t } = useI18n();
    const { projects, isLoading, fetchProjects } = useProject();
    const { fetchTeams: fetchTeamsData, initialized } = useTeamContext();

    const [showComposer, setShowComposer] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    /* ── Initial load ── */
    useEffect(() => {
        fetchProjects();
        if (!initialized) fetchTeamsData();
    }, [fetchProjects, initialized, fetchTeamsData]);

    /* ── Pagination ── */
    const visibleProjects = projects.slice(0, page * PER_PAGE);
    useEffect(() => {
        setHasMore(projects.length > page * PER_PAGE);
    }, [projects.length, page]);

    /* ── Reset page when projects list is replaced (new fetch) ── */
    useEffect(() => {
        setPage(1);
    }, [projects]);

    /* ── Infinite scroll ── */
    const handleScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (!hasMore || isLoading) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
            setPage(prev => prev + 1);
        }
    }, [hasMore, isLoading]);

    /* ── Refresh ── */
    const handleRefresh = async () => {
        setIsRefreshing(true);
        setPage(1);
        await fetchProjects();
        setIsRefreshing(false);
    };

    /* ── Composer ── */
    const handleCreate = () => setShowComposer(true);
    const handleCloseComposer = () => setShowComposer(false);
    const handleComposerSuccess = () => {
        setShowComposer(false);
        fetchProjects(); // re-fetch so widget shows the newly created project
    };

    /* ── Shared render pieces (compact + expanded share the same markup) ── */
    const renderHeader = (onStretch) => (
        <div className="widget-header proj-header">
            <span className="widget-title proj-title">{t('navigation.projects')}</span>
            <div className="header-right">
                <PlusIcon
                    className="widget-icon proj-icon"
                    title={t('common.actions.create')}
                    onClick={handleCreate}
                />
                <RefreshIcon
                    className={`widget-icon proj-icon${isRefreshing ? ' proj-icon-spinning' : ''}`}
                    title={t('common.actions.refresh')}
                    onClick={handleRefresh}
                />
                <ExpandIcon
                    className="widget-icon proj-icon"
                    title={isExpanded ? t('common.actions.collapse') : t('common.actions.expand')}
                    onClick={onStretch}
                />
            </div>
        </div>
    );

    const renderBody = (isActive = true) => (
        <div className="widget-body proj-body" onScroll={isActive ? handleScroll : undefined}>
            {(isLoading || isRefreshing) ? (
                <ProjSkeleton />
            ) : projects.length === 0 ? (
                <div className="proj-empty">{t('tables.empty.no_projects')}</div>
            ) : (
                <>
                    {visibleProjects.map(project => (
                        <div
                            key={project.id}
                            className="proj-item"
                            onClick={() => setSelectedProject(project)}
                        >
                            <div className="proj-name">{project.title}</div>
                            <div className="proj-creator">
                                {t('common.labels.created_by')}: {project.project_manager?.name || project.creator?.name || t('common.misc.unknown')}
                            </div>
                        </div>
                    ))}
                    {hasMore && (
                        <div className="proj-load-more">
                            <div className="proj-load-spinner" />
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            {/* ── Compact widget ── */}
            <div className="widget-card projects-widget">
                {renderHeader(() => setIsExpanded(true))}
                {renderBody(!isExpanded)}
            </div>

            {/* ── Expanded overlay (same pattern as Calendar & News Feed) ── */}
            {isExpanded && (
                <div className="nfw-expanded-overlay" onClick={() => setIsExpanded(false)}>
                    <div className="nfw-expanded-panel" onClick={e => e.stopPropagation()}>
                        {renderHeader(() => setIsExpanded(false))}
                        {renderBody(true)}
                    </div>
                </div>
            )}

            {/* ── Project Details Modal ── */}
            <ProjectDetailsModal
                isOpen={!!selectedProject}
                project={selectedProject}
                onClose={() => setSelectedProject(null)}
            />

            {/* ── Create Project Modal ── */}
            {showComposer && (
                <ProjectComposer
                    isOpen={showComposer}
                    onClose={handleCloseComposer}
                    onSuccess={handleComposerSuccess}
                    project={null}
                />
            )}
        </>
    );
};

export default ProjectsWidget;
